/**
 * SignatureCapture Component
 * Digital signature capture for daily log submissions and approvals
 * BuilderBel Construction Project Management System
 */

import { Check, RefreshCw, X } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface SignatureCaptureProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (signature: string) => void;
  title?: string;
  description?: string;
  submitLabel?: string;
  isSubmitting?: boolean;
  signerName?: string;
  signerRole?: string;
}

export const SignatureCapture: React.FC<SignatureCaptureProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title = 'Digital Signature',
  description = 'Sign below to confirm the accuracy of this daily log.',
  submitLabel = 'Submit',
  isSubmitting = false,
  signerName = '',
  signerRole = '',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null);
  const [lastPosition, setLastPosition] = useState<{ x: number; y: number } | null>(null);

  // Initialize canvas
  useEffect(() => {
    if (isOpen && canvasRef.current) {
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      if (context) {
        // Set canvas size
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * window.devicePixelRatio;
        canvas.height = rect.height * window.devicePixelRatio;
        context.scale(window.devicePixelRatio, window.devicePixelRatio);

        // Set drawing style
        context.strokeStyle = '#1F2937'; // gray-900
        context.lineWidth = 2;
        context.lineCap = 'round';
        context.lineJoin = 'round';

        // Clear canvas
        context.fillStyle = '#FFFFFF';
        context.fillRect(0, 0, canvas.width, canvas.height);

        // Draw signature line
        context.beginPath();
        context.strokeStyle = '#E5E7EB'; // gray-200
        context.moveTo(20, rect.height - 30);
        context.lineTo(rect.width - 20, rect.height - 30);
        context.stroke();

        // Reset stroke style
        context.strokeStyle = '#1F2937';

        setCtx(context);
        setHasSignature(false);
      }
    }
  }, [isOpen]);

  // Get position from event
  const getPosition = useCallback(
    (e: React.MouseEvent | React.TouchEvent): { x: number; y: number } | null => {
      if (!canvasRef.current) return null;

      const rect = canvasRef.current.getBoundingClientRect();
      let clientX: number;
      let clientY: number;

      if ('touches' in e) {
        if (e.touches.length === 0) return null;
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }

      return {
        x: clientX - rect.left,
        y: clientY - rect.top,
      };
    },
    []
  );

  // Start drawing
  const handleStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      const pos = getPosition(e);
      if (pos && ctx) {
        setIsDrawing(true);
        setLastPosition(pos);
        ctx.beginPath();
        ctx.moveTo(pos.x, pos.y);
      }
    },
    [ctx, getPosition]
  );

  // Draw
  const handleMove = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      if (!isDrawing || !ctx || !lastPosition) return;

      const pos = getPosition(e);
      if (pos) {
        ctx.beginPath();
        ctx.moveTo(lastPosition.x, lastPosition.y);
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
        setLastPosition(pos);
        setHasSignature(true);
      }
    },
    [isDrawing, ctx, lastPosition, getPosition]
  );

  // Stop drawing
  const handleEnd = useCallback(() => {
    setIsDrawing(false);
    setLastPosition(null);
  }, []);

  // Clear signature
  const handleClear = useCallback(() => {
    if (!canvasRef.current || !ctx) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, rect.width, rect.height);

    // Redraw signature line
    ctx.beginPath();
    ctx.strokeStyle = '#E5E7EB';
    ctx.moveTo(20, rect.height - 30);
    ctx.lineTo(rect.width - 20, rect.height - 30);
    ctx.stroke();

    // Reset stroke style
    ctx.strokeStyle = '#1F2937';

    setHasSignature(false);
  }, [ctx]);

  // Submit signature
  const handleSubmit = useCallback(() => {
    if (!canvasRef.current || !hasSignature) return;

    // Get signature as base64
    const signatureData = canvasRef.current.toDataURL('image/png');
    onSubmit(signatureData);
  }, [hasSignature, onSubmit]);

  // Handle close
  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      handleClear();
      onClose();
    }
  }, [isSubmitting, handleClear, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[150]" onClick={handleClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-[151] flex items-center justify-center p-4">
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              {(signerName || signerRole) && (
                <p className="text-sm text-gray-500">
                  {signerName}
                  {signerRole && <span className="text-gray-400"> - {signerRole}</span>}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-sm text-gray-600 mb-4">{description}</p>

            {/* Signature Canvas */}
            <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
              <canvas
                ref={canvasRef}
                className="w-full h-48 cursor-crosshair touch-none"
                onMouseDown={handleStart}
                onMouseMove={handleMove}
                onMouseUp={handleEnd}
                onMouseLeave={handleEnd}
                onTouchStart={handleStart}
                onTouchMove={handleMove}
                onTouchEnd={handleEnd}
              />
            </div>

            {/* Instructions */}
            <div className="flex items-center justify-between mt-3">
              <p className="text-xs text-gray-400">
                {hasSignature ? 'Signature captured' : 'Use your mouse or finger to sign above'}
              </p>
              {hasSignature && (
                <button
                  type="button"
                  onClick={handleClear}
                  disabled={isSubmitting}
                  className="text-sm text-gray-500 hover:text-gray-700 flex items-center space-x-1 disabled:opacity-50"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  <span>Clear</span>
                </button>
              )}
            </div>

            {/* Legal Notice */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">
                By signing, I certify that the information in this daily log is accurate and
                complete to the best of my knowledge. This electronic signature has the same legal
                effect as a handwritten signature.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!hasSignature || isSubmitting}
              className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  <span>{submitLabel}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default SignatureCapture;
